<script setup lang="ts">
import { captureException } from '@sentry/vue';
import { useThrottleFn, watchOnce } from '@vueuse/core';
import { minutesToMilliseconds } from 'date-fns';

import { useCurrentSignatureRequestKey, isFetchError, useEnvironmentInfo, useModals, APP_ROUTES, useFeature, Decisions, AppFeature, ScreenSizes, usePosthog } from '~~core';
import { useFetchRequirementSteps, InitialsModal, RequirementRefusalModal, useFetchRequirement, saveRequirement, useRequirement } from '~~requirement';

import DocumentPreview from '../../../../../components/DocumentPreview/DocumentPreview.vue';
import DocumentToolbar from '../../../../../components/DocumentToolbar/DocumentToolbar.vue';
import DocumentActions from '../../../../../components/DocumentActions/DocumentActions.vue';
import DocumentSumarizeAction from '../../../../../components/DocumentSumarizeAction/DocumentSumarizeAction.vue';
import DocumentSumarizeContent from '../../../../../components/DocumentSumarizeContent/DocumentSumarizeContent.vue';
import RubricateFooter from '../../../../../components/RubricateFooter/RubricateFooter.vue';
import SignatureStamp, { SignatureStampPublicAPI } from '../../../../../components/SignatureStamp/SignatureStamp.vue';
import OptionsModal from '../../../../../components/OptionsModal/OptionsModal.vue';
import PositionSignModal from '../../../../../components/PositionSignModal/PositionSignModal.vue';
import { useSignaturePositionSign } from '../../../../../composables/use-signature-position-sign';
import { useCurrentDocumentKey } from '../../../../../composables/use-current-document-key';
import { useFetchDocument, useFetchDocumentsList } from '../../../../../repositories/document';
import { type OptionsEvents, useDocumentActions } from '../../../../../composables/use-document-actions';

const modalName = {
  options: 'options',
  initials: 'initials',
  refusal: 'refusal',
};

const localePath = useLocalePath();
const { openModal, isModalOpen, closeModal } = useModals();
const gtm = useGtm();
const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { documentKey } = useCurrentDocumentKey();
const { isFromSignerArea } = useEnvironmentInfo();

const { steps } = useFetchRequirementSteps(signatureRequestKey);
const { document, error } = useFetchDocument(signatureRequestKey, documentKey);
const { documents: documentList, isLoading: isLoadingList } = useFetchDocumentsList(signatureRequestKey);

const hasMultipleDocuments = computed(() => documentList.value && documentList.value.length > 1);
const hideBackButton = computed(() => !isFromSignerArea.value && !hasMultipleDocuments.value);
const hasInitials = computed(() => steps.value?.all.includes('initials'));

const { requirement } = useFetchRequirement('rubricate', signatureRequestKey, { staleTime: minutesToMilliseconds(10) });
const { artefact, requirements: cleanRequirements } = useRequirement(requirement);

const {
  rubricates = ref({ initials: '', manuscript: '', key: '', expireAt: '' }),
  isRubricateError = ref(false),
  areArtifactsValid = ref(false),
  refreshRubricates = () => {},
  widgetSignatureModal = '',
  isLoadingRubricate = ref(false),
  canShowPositioned = ref(false),
  rubricateTotal = ref(0),
  rubricatesInDocument = ref([]),
  signatureTotal = ref(0),
  documentsToRubricate = ref([{ key: '' }]),
  documentIndex = ref(0),
  positionedKinds = ref([]),
  artefacts = ref({}),
  overrideExpirationDate = ref(false),
} = useSignaturePositionSign(signatureRequestKey, documentKey, cleanRequirements);

const scrollPaddingStyles = computed(() => (canShowPositioned.value ? {
  '--scroll-padding': 'var(--space-regular) 0 150px 0',
} : {}));

const rubricateSigned = ref(0);

const needInitialsFont = computed(() => !!canShowPositioned?.value && (hasInitials.value || rubricateTotal.value));
const headLinks = computed(() => [
  needInitialsFont.value && {
    href: 'https://fonts.googleapis.com/css2?family=Over+the+Rainbow&display=swap',
    rel: 'stylesheet',
  },
].filter(Boolean));

useHead({ link: headLinks });

const signatureStampEl = ref<SignatureStampPublicAPI>();

function generateUserActions() {
  const actions: OptionsEvents[] = [
    'help',
    'download',
  ];

  if (document.value?.refusable) actions.unshift('refusal');

  return actions;
}

const enabledActions = computed(() => generateUserActions());
const { actionDefinitions } = useDocumentActions(enabledActions, document, { text: 'long' });

function goBack() {
  const goToSignerArea = isFromSignerArea.value && !hasMultipleDocuments.value;
  const documentListPath = localePath({
    name: 'signatureRequestKind-signatureRequestKey-documents',
    params: {
      signatureRequestKey: signatureRequestKey.value,
      documentKey: documentKey.value,
    },
  });

  navigateTo(goToSignerArea ? APP_ROUTES.signerArea : documentListPath, { external: goToSignerArea });
}

function handleCloseOptionsModal() {
  if (isModalOpen(modalName.options)) closeModal();
}

const canShowPositionSignModal = computed(() => isModalOpen(widgetSignatureModal) && !areArtifactsValid.value);

function handleSignatureClick() {
  openModal(widgetSignatureModal);
}

function handleSignatureConfirm(validateArtefacts: boolean) {
  overrideExpirationDate.value = validateArtefacts;
  closeModal();
  refreshRubricates();
}

function goToRequirementsSummary() {
  navigateTo(localePath({
    name: 'signatureRequestKind-signatureRequestKey-requirement-summary',
    params: {
      signatureRequestKind: 'notarial',
    },
  }));
}

function handleAgree() {
  if (hasInitials.value) openModal(modalName.initials);
  else goToRequirementsSummary();
}

watch(error, () => {
  const statusCode = isFetchError(error.value) ? error.value.statusCode : 500;

  throw createError({ statusCode, fatal: true, cause: error });
});

const isPdfLoading = ref(true);
const currentPageNumber = ref(1);
const totalPages = ref(1);

const isPdfLoadError = ref(false);
const isPdfReady = computed(() => !isPdfLoading.value && !isPdfLoadError.value);
const forceLegacy = computed(() => document.value?.forcePreviewPages);

function handlePagesLoad(pagesCount: number | undefined) {
  if (pagesCount) {
    isPdfLoading.value = false;
    totalPages.value = pagesCount;
  }
}

function goToNextStamp() {
  signatureStampEl.value?.nextStamp();
}

const wrapperPositionSign = ref<HTMLElement>();

function handleMoveToTop() {
  try {
    const pdfViewerEl = wrapperPositionSign.value?.querySelector('#viewer')?.parentElement;

    if (!pdfViewerEl) throw new Error(`pdfViewerEl is ${typeof pdfViewerEl}`);
    pdfViewerEl.scroll({ top: 0, behavior: 'smooth' });
  } catch (err) {
    captureException(err);
  }
}

const isLastDocument = computed(
  () => !!canShowPositioned?.value && (documentIndex.value === documentsToRubricate.value.length - 1),
);

function goToDocument(nextDocumentKey: string) {
  navigateTo(localePath({
    name: 'signatureRequestKind-signatureRequestKey-documents-documentKey',
    params: {
      signatureRequestKey: signatureRequestKey.value,
      documentKey: nextDocumentKey,
    },
  }));
}

const nextClickHandler = async () => {
  if (!canShowPositioned?.value) return;

  if (isLastDocument.value) {
    if (!artefact?.value && !!rubricates.value) {
      await saveRequirement('rubricate', signatureRequestKey.value, { rubricateKey: rubricates.value.key });
      gtm?.trackEvent({
        event: 'ga-send',
        ga_event_category: 'positioned-sign-signer',
        ga_event_name: 'button-done-positioned-sign',
      });
    }
    goToRequirementsSummary();
  } else {
    const nextIndex = toValue(documentIndex) + 1;
    const nextDocument = toValue(documentsToRubricate)[nextIndex];

    if (!nextDocument) return;

    goToDocument(nextDocument?.key);
  }
};

const scaleChangingHandler = useThrottleFn(() => {
  if (!signatureStampEl.value || !isPdfReady.value) return;

  signatureStampEl.value.calculateCoordinatesFromEachPositionStamp();
}, 50);

const reload = () => window.location.reload();

const isPdfVisualizerLegacy = ref(false);

const isAiSumarizerVisible = ref(false);

// EXPERIMENT_DOCUMENT_AI_SUMMARY
// TODO: Remove isEnabled computed from experiment.
const { isEnabled: isExperimentDocumentAiSummaryEnabled } = useFeature(Decisions.EXPERIMENT_DOCUMENT_AI_SUMMARY);

const isMobile = window.innerWidth < ScreenSizes.MEDIUM;

// EXPERIMENT_DOCUMENT_AI_SUMMARY
// TODO: Check if keep code below
const posthog = usePosthog();
const shouldDocumentAiSummaryRender = ref(false);
const hasRubricate = computed(() => !!steps.value?.all.includes('rubricate'));

const captureOnExperimentDocumentAiSummaryExposed = () => {
  if (!isExperimentDocumentAiSummaryEnabled.value) return;

  posthog.capture('feature_exposed', {
    feature_name: Decisions.EXPERIMENT_DOCUMENT_AI_SUMMARY,
    is_positioned_sign: hasRubricate.value,
    steps: (steps.value?.all || []).join(','),
  });
};

const checkDocumentAiSummaryRender = () => {
  captureOnExperimentDocumentAiSummaryExposed();

  shouldDocumentAiSummaryRender.value = !hasRubricate.value;
};

watchOnce(steps, checkDocumentAiSummaryRender, { flush: 'post' });
onMounted(checkDocumentAiSummaryRender);
</script>

<template>
  <div :class="$style.singleDocument">
    <DocumentToolbar
      :loading="isLoadingList"
      :signers-count="document?.totalSignatures"
      :signed-count="document?.totalSigned"
      :can-show-positioned="canShowPositioned"
      :rubricate-total="rubricateTotal"
      :current-document="documentIndex + 1"
      :hide-back-button="hideBackButton"
      open-external-modal-options
      @open-options-modal="openModal(modalName.options)"
      @back="goBack()"
    />

    <div style="display: flex; height: 100%;">
      <div
        ref="wrapperPositionSign"
        :class="$style.viewer"
      >
        <DocumentPreview
          v-if="document"
          :document-pdf-url="document.pdfUrl"
          :document-key="documentKey"
          :container-class="$style.preview"
          :relative="!rubricateTotal"
          :custom-retry="canShowPositioned ? reload : undefined"
          :limit-pdf-to-scale-1="canShowPositioned"
          :style="scrollPaddingStyles"
          :legacy="forceLegacy"
          @scale-changing="scaleChangingHandler"
          @pages-load="handlePagesLoad"
          @page-change="currentPageNumber = $event"
          @error="isPdfLoadError = true"
          @update:legacy="isPdfVisualizerLegacy = $event"
        />
      </div>

      <AppFeature
        v-if="!isMobile && shouldDocumentAiSummaryRender"
        :name="Decisions.EXPERIMENT_DOCUMENT_AI_SUMMARY"
      >
        <DocumentSumarizeAction
          v-show="!isAiSumarizerVisible"
          floating
          @click="() => isAiSumarizerVisible = !isAiSumarizerVisible"
        />

        <DocumentSumarizeContent
          :visible="isAiSumarizerVisible"
          @close="isAiSumarizerVisible = false"
        />
      </AppFeature>
    </div>

    <!-- EXPERIMENT_DOCUMENT_AI_SUMMARY -->
    <!-- TODO: Remove isExperimentDocumentAiSummaryEnabled from style prop -->
    <DocumentActions
      v-if="!isLoadingList && !hasMultipleDocuments && !canShowPositioned"
      :class="{[$style.splitedScreen]: (isExperimentDocumentAiSummaryEnabled && isAiSumarizerVisible && !isMobile)}"
      :documents-length="documentList.length"
      show-options-action
      @open-options-modal="openModal(modalName.options)"
      @sign-complete="handleAgree()"
    />

    <SignatureStamp
      v-if="canShowPositioned && isPdfReady"
      ref="signatureStampEl"
      :are-rubricates-ready="!isLoadingRubricate && areArtifactsValid"
      :wrapper-position-sign="wrapperPositionSign"
      :initial-signature-stamps="rubricatesInDocument"
      :rubricates="rubricates"
      :artefact="artefact"
      :is-loading="isLoadingRubricate"
      :is-error="isRubricateError"
      :document-index="documentIndex"
      @signatures-total="signatureTotal = $event"
      @total-signed="rubricateSigned = $event"
      @signature-click="handleSignatureClick"
    />

    <div
      v-if="canShowPositioned"
      :class="$style.footer"
    >
      <RubricateFooter
        :loading="isLoadingRubricate || isPdfLoading"
        :current-page="currentPageNumber"
        :pages-count="totalPages"
        :document-rubricates="rubricateSigned"
        :document-total-rubricates="signatureTotal"
        :is-last-document="isLastDocument"
        :hide-page-count="forceLegacy || isPdfVisualizerLegacy"
        @to-top="handleMoveToTop"
        @to-next-field="goToNextStamp"
        @go-next="nextClickHandler"
      />
    </div>

    <OptionsModal
      v-if="document && isModalOpen(modalName.options)"
      :action-list="actionDefinitions"
      @open-refusal-modal="openModal(modalName.refusal)"
      @close="handleCloseOptionsModal()"
    />

    <InitialsModal
      v-if="!canShowPositioned && isModalOpen(modalName.initials)"
      @complete="goToRequirementsSummary()"
      @close="closeModal()"
    />

    <RequirementRefusalModal
      v-if="document && isModalOpen(modalName.refusal)"
      :document-keys="[document?.key]"
      @close="closeModal()"
    />

    <PositionSignModal
      v-if="canShowPositionSignModal"
      :is-modal-open="isModalOpen(widgetSignatureModal)"
      :positioned-kinds="positionedKinds"
      :artefacts="artefacts"
      @close="closeModal()"
      @next="handleSignatureConfirm"
    />
  </div>
</template>

<style lang="scss" module>
.singleDocument {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
}

.preview {
  padding: var(--space-large) 0 calc(var(--space-large-x) * 3);

  @include breakpoint('medium') {
    padding-bottom: calc(var(--space-large-xx) * 2);
  }
}

.viewer {
  flex: 1;
  background: var(--color-neutral-50);
}

.footer {
  position: fixed;
  z-index: var(--zindex-fixed);
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  margin: auto;
  padding: var(--space-regular);
  gap: var(--space-regular);
  background: var(--color-neutral-0);
  box-shadow: var(--shadow-top-medium);
}

@include breakpoint('small') {
  .footer {
    flex-wrap: nowrap;
    gap: 0;
  }
}

.splitedScreen {
  margin-left: -228px;
}
</style>
